// React Components and Hooks
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Box, Typography, Skeleton } from "@mui/material";

import { formatMoney, numberWithCommas } from "../../setup/helpers";
import vars from "../../assets/core/variables-mui";
import { getDisconnectMetricsQueryParams } from "./dashboardPeriodContext";
import {
  fetchDashboardResource,
  getDashboardErrorMessage,
  isDashboardAuthPending,
} from "./dashboardFetch";

const dash = {
  muted: vars.grey600,
  text: vars.grey900,
  panelBorder: vars.grey300,
  risk: vars.error,
};

/**
 * Disconnect metrics from GET /disconnect-metrics where the backend applies
 * startDate/endDate to AccountDisconnect.disconnectDate.
 *
 * We only surface fields that are not mixed with unrelated windows (e.g. "today"
 * or calendar week). See apl-account-service getAccountDisconnectMetrics.
 */
function DisconnectPeriodMetricsCard({ periodLabel, period, cardClassName, cardTitleClassName }) {
  const params = useMemo(() => getDisconnectMetricsQueryParams(period), [period]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outstandingBalance, setOutstandingBalance] = useState(null);
  const [agedDisconnects, setAgedDisconnects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetchDashboardResource(() =>
          axios.get("/disconnect-metrics", { params })
        );
        if (cancelled) return;
        const data = res.data || {};
        const bal = parseFloat(data.totalOutstandingBalance);
        const aged = Number(data.agedDisconnects);
        setOutstandingBalance(Number.isFinite(bal) ? bal : 0);
        setAgedDisconnects(Number.isFinite(aged) ? aged : 0);
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setError(getDashboardErrorMessage(err, "Could not load disconnect metrics"));
          setOutstandingBalance(null);
          setAgedDisconnects(null);
        }
      } finally {
        if (!cancelled && !authPending) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params.startDate, params.endDate]);

  const rows = useMemo(
    () => [
      {
        key: "balance",
        label: "Outstanding balance",
        sub: "Disconnected accounts whose disconnect date falls in this range",
        value: formatMoney(outstandingBalance ?? 0),
        align: "right",
      },
      {
        key: "aged",
        label: "Aged / still open",
        sub: "Scheduled disconnect at least 8 days ago, not completed (in range)",
        value: numberWithCommas(agedDisconnects ?? 0),
        align: "right",
      },
    ],
    [outstandingBalance, agedDisconnects]
  );

  return (
    <Box className={cardClassName} sx={{ display: "flex", flexDirection: "column" }}>
      <Typography className={cardTitleClassName}>Disconnect activity · {periodLabel}</Typography>
      <Typography sx={{ fontSize: "0.65rem", color: dash.muted, mb: 1, lineHeight: 1.35 }}>
        Scheduled disconnect date between {params.startDate} and {params.endDate}
      </Typography>

      {loading ? (
        <Box sx={{ py: 0.5 }}>
          <Skeleton variant="text" width="90%" height={22} />
          <Skeleton variant="text" width="70%" height={18} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="90%" height={22} sx={{ mt: 1.5 }} />
          <Skeleton variant="text" width="50%" height={18} sx={{ mt: 1 }} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: "0.875rem", color: dash.risk }}>{error}</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75, borderBottom: `1px solid ${dash.panelBorder}`, pb: 0.75 }}>
            <Typography sx={{ fontSize: "0.7rem", color: dash.muted, flex: 1 }}>Metric</Typography>
            <Typography sx={{ fontSize: "0.7rem", color: dash.muted, width: 96, textAlign: "right" }}>Value</Typography>
          </Box>
          <Box>
            {rows.map((row) => (
              <Box key={row.key} sx={{ py: 0.65 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: dash.text, flex: 1, fontWeight: 500 }}>{row.label}</Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      width: 96,
                      textAlign: row.align,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.65rem", color: dash.muted, mt: 0.35, lineHeight: 1.35 }}>{row.sub}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

DisconnectPeriodMetricsCard.propTypes = {
  periodLabel: PropTypes.string.isRequired,
  period: PropTypes.string.isRequired,
  cardClassName: PropTypes.string.isRequired,
  cardTitleClassName: PropTypes.string.isRequired,
};

export default DisconnectPeriodMetricsCard;
